import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsWriteoffs,
  type GoodsWriteoffItem,
} from '@/api/goodsWriteoffs';
import { useDeferredValue } from 'react';
import { nomenclature, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  writeoff_reason: string;
  reason_text: string;
  comment: string;
  items: GoodsWriteoffItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  writeoff_reason: 'SHORTAGE',
  reason_text: '',
  comment: '',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsWriteoffEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.writeoff.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.writeoff.manage');
  const docQuery = useQuery({
    queryKey: ['goods-writeoff', id],
    queryFn: () => goodsWriteoffs.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'goods-writeoff', (cid) => goodsWriteoffs.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // Вкладки «Рядки/Рахунки обліку» + дт-кт (рішення власника 2026-08-09).
  const [viewTab, setViewTab] = useState<'lines' | 'accounts'>('lines');
  // 2026-08-12 (рішення власника): попап номенклатури з мультивибором, як у переміщенні.
  const [pickerOpen, setPickerOpen] = useState(false);
  const numberPreview = useNumberPreview('doc.goods_writeoffs', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomenclature.listAll(),
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        writeoff_reason: d.writeoff_reason,
        reason_text: d.reason_text ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          amount: it.amount ? String(it.amount) : undefined,
          // 0415: склад рядка (fallback стара шапка для legacy-доків).
          warehouse_id: it.warehouse_id ?? d.warehouse_id ?? null,
          account_code: it.account_code ?? null,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const totalCost = form.items.reduce((s, e) => s + Number(e.amount || 0), 0);

  const create = useMutation({
    mutationFn: () => goodsWriteoffs.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      writeoff_reason: form.writeoff_reason,
      reason_text: form.reason_text || null,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-writeoffs'] });
      navigate(`/goods-writeoffs/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsWriteoffs.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      writeoff_reason: form.writeoff_reason,
      reason_text: form.reason_text || null,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-writeoff', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsWriteoffs.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        writeoff_reason: form.writeoff_reason,
        reason_text: form.reason_text || null,
        comment: form.comment || null,
        items: form.items,
      });
      try {
        await goodsWriteoffs.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/goods-writeoffs/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-writeoffs'] });
      navigate(`/goods-writeoffs/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await goodsWriteoffs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        writeoff_reason: form.writeoff_reason,
        reason_text: form.reason_text || null,
        comment: form.comment || null,
        items: form.items,
      });
      await goodsWriteoffs.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-writeoff', id] });
      qc.invalidateQueries({ queryKey: ['goods-writeoffs'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsWriteoffs.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-writeoff', id] });
      qc.invalidateQueries({ queryKey: ['goods-writeoffs'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsWriteoffs.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-writeoffs'] });
      navigate('/goods-writeoffs');
    },
  });

  function setItem(i: number, patch: Partial<GoodsWriteoffItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.writeoff.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати списання товарів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-writeoffs')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.886 (новий дизайн, Закон #10): «ще дій» не було; дт-кт/історія — у тулбарі/рейці.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) + (form.writeoff_reason ? 1 : 0) },
    { id: 'items', title: 'Товари', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове списання' : `Списання: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/goods-writeoffs')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/goods-writeoffs/${id}/print`)}
          entriesDoc={{ type: 'doc.goods_writeoffs', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-writeoffs/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_writeoffs', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Причина *</label>
          <select value={form.writeoff_reason}
                  onChange={(e) => setForm({ ...form, writeoff_reason: e.target.value })}
                  disabled={isPosted}>
            <option value="SHORTAGE">Нестача</option>
            <option value="DAMAGE">Брак / псування</option>
            <option value="USAGE">На власні потреби</option>
          </select>
        </div>
      </div>

      <div className="field">
        <label>Опис причини (необов'язково)</label>
        <input value={form.reason_text}
               onChange={(e) => setForm({ ...form, reason_text: e.target.value })}
               disabled={isPosted}
               placeholder="напр., Розбито при перевезенні" />
      </div>

      </DocBlock>

      <DocBlock index={2} title="Товари"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            title="Підбір з довідника з залишками; галочками можна обрати кілька товарів одразу">
            + додати номенклатуру
          </button>
        ) : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab(viewTab === 'accounts' ? 'lines' : 'accounts')}>
            Рахунки обліку</button>
        </div>
      </div>
      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 200 }}
                    title="Рахунок запасів рядка — звідки списується товар (порожньо — авто: 281)">
                  Рах. обліку (запаси)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const nomAcc = nomListQ.data?.items.find((n) => n.id === it.nomenclature_id);
                return (
                  <tr key={i}>
                    <td>{nomAcc?.name ?? '—'}</td>
                    <td>
                      <input value={it.account_code ?? ''} maxLength={10}
                        onChange={(e) => setForm((f) => ({ ...f,
                          items: f.items.map((row, idx) => idx === i
                            ? { ...row, account_code: e.target.value || null } : row) }))}
                        disabled={isPosted}
                        placeholder="авто: 281"
                        style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожнє поле — рахунок підбирається автоматично (типовий 281).
          </p>
        </div>
      )}
      {viewTab === 'lines' && (
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 190 }}>Склад</th>
            <th style={{ width: 110 }}>Кількість</th>
            <th style={{ width: 140 }}>Собівартість</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  kind="goods"
                  onChange={(v) => setItem(i, { nomenclature_id: v })}
                />
              </td>
              <td>
                <select value={it.warehouse_id ?? ''}
                  onChange={(e) => setItem(i, { warehouse_id: e.target.value || null })}
                  disabled={isPosted}>
                  <option value="">—</option>
                  {warehousesQ.data?.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <input type="number" step="0.0001" value={it.quantity}
                       onChange={(e) => setItem(i, { quantity: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                {isPosted && it.amount
                  ? fmt(Number(it.amount))
                  : <CostPreviewCell orgId={form.organization_id} item={it}
                      docDate={form.document_date} />}
              </td>
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        {isPosted && (
          <tfoot>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right' }}>Усього:</td>
              <td style={{ textAlign: 'right' }}>{fmt(totalCost)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </TchTable>
      )}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          multiSelect
          onPickMany={(rows) => {
            setForm((f) => ({
              ...f,
              items: [...f.items, ...rows.map((r, k) => ({
                line_number: f.items.length + k + 1,
                nomenclature_id: r.nomenclature_id,
                quantity: '1',
                // Джерело — склад рядка залишків (якщо був обраний).
                warehouse_id: r.warehouse_id || null,
              }))],
            }));
            setPickerOpen(false);
          }}
          onPick={() => undefined}
          onClose={() => setPickerOpen(false)}
        />
      )}

      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.goods_writeoffs" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> при проведенні собівартість визначається автоматично
          (FIFO або WAVG залежно від облікової політики), створюються рухи `-` у регістрах
          товарів і партій, бухпроводка <strong>Дт 947 — Кт 281</strong> на сумарну собівартість.
          Списати можна лише вільний залишок (з урахуванням резервів під замовлення).
        </p>
      )}
    </div>
  );
}

/**
 * Жива попередня собівартість рядка (2026-08-12, рішення власника).
 * До проведення читає ті самі партії (FIFO/WAVG), що й проведення, і
 * показує оцінку з поміткою «попередньо»; фінальна сума — при проведенні.
 */
function CostPreviewCell({ orgId, item, docDate }: {
  orgId: string;
  item: GoodsWriteoffItem;
  docDate: string;
}) {
  const qty = useDeferredValue(item.quantity);
  const ready = !!(orgId && item.nomenclature_id && item.warehouse_id && Number(qty) > 0);
  const q = useQuery({
    queryKey: ['wo-cost-preview', orgId, item.warehouse_id, item.nomenclature_id, qty, docDate],
    queryFn: () => goodsWriteoffs.costPreview({
      organization_id: orgId,
      warehouse_id: item.warehouse_id!,
      nomenclature_id: item.nomenclature_id,
      quantity: String(qty),
      on_date: new Date(docDate).toISOString(),
      characteristic_id: item.characteristic_id ?? undefined,
      series_id: item.series_id ?? undefined,
    }),
    enabled: ready,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  if (!ready) return <span style={{ color: '#94a3b8' }}>—</span>;
  if (q.isLoading) return <span style={{ color: '#94a3b8' }}>…</span>;
  if (!q.data?.available) {
    return <span style={{ color: 'var(--badge-red-fg)', fontSize: '0.8rem' }}>недостатньо залишку</span>;
  }
  return (
    <span title={`Оцінка за партіями (${q.data.method === 'FIFO' ? 'FIFO' : 'середньозважена'}) на дату документа; точна сума визначиться при проведенні`}>
      {fmt(Number(q.data.cost))}{' '}
      <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>попередньо</span>
    </span>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
